import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useToast } from '@/components/ui/use-toast';
import {
  ArrowLeft,
  HelpCircle,
  Mail,
  MessageSquare,
  Phone,
  FileText,
  ExternalLink,
  Send
} from 'lucide-react';

export default function CompanySupport() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    email: 'contact@company.com',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      toast({
        title: 'Message Sent',
        description: 'Our support team will respond within 24 hours.',
      });
      setFormData({ ...formData, subject: '', message: '' });
    }, 1000);
  };

  const faqs = [
    {
      question: 'How long does it take for a problem to be reviewed?',
      answer: 'Our analysts typically review submissions within 2-3 business days. You will receive a notification when your submission status changes.',
    },
    {
      question: 'What happens after my problem is accepted?',
      answer: 'Accepted problems are converted into Problem Bank entries and assigned to student squads. You will be notified when a squad begins working on your problem.',
    },
    {
      question: 'How is the estimated impact calculated?',
      answer: 'Our analysts verify your estimated impact against provided data. If accepted, the verified figure becomes the baseline for solution ROI calculations.',
    },
    {
      question: 'What are the costs for submitting problems?',
      answer: 'Initial submission is free. If your problem is accepted and a venture is created, you will enter a revenue share agreement with the new venture.',
    },
    {
      question: 'Can I submit multiple problems?',
      answer: 'Yes, you can submit as many problems as you\'d like. Each submission is reviewed independently.',
    },
    {
      question: 'How do I update my company information?',
      answer: 'Go to Company Profile in the sidebar to update your company details and contact information.',
    },
  ];

  return (
    <div className="space-y-6 w-full max-w-4xl mx-auto">
      <Button variant="ghost" onClick={() => navigate('/company/dashboard')} className="gap-2">
        <ArrowLeft className="h-4 w-4" />
        Back to Dashboard
      </Button>

      <h1 className="text-3xl font-bold">Help & Support</h1>

      <div className="grid grid-cols-3 gap-6">
        {/* Contact Options */}
        <div className="col-span-1 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Contact Us</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted cursor-pointer">
                <Mail className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium">Email</p>
                  <p className="text-xs text-muted-foreground">support@vettandventure.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted cursor-pointer">
                <MessageSquare className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium">Live Chat</p>
                  <p className="text-xs text-muted-foreground">Mon-Fri, 9am-5pm EAT</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted cursor-pointer">
                <Phone className="h-5 w-5 text-purple-600" />
                <div>
                  <p className="font-medium">Phone</p>
                  <p className="text-xs text-muted-foreground">+254 745 665 732</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted cursor-pointer">
                <FileText className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="font-medium">Documentation</p>
                  <p className="text-xs text-muted-foreground">Guides & tutorials</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <a href="#" className="flex items-center justify-between p-2 hover:bg-muted rounded">
                <span>Submission Guidelines</span>
                <ExternalLink className="h-4 w-4" />
              </a>
              <a href="#" className="flex items-center justify-between p-2 hover:bg-muted rounded">
                <span>V&V Standard</span>
                <ExternalLink className="h-4 w-4" />
              </a>
              <a href="#" className="flex items-center justify-between p-2 hover:bg-muted rounded">
                <span>FAQ</span>
                <ExternalLink className="h-4 w-4" />
              </a>
              <a href="#" className="flex items-center justify-between p-2 hover:bg-muted rounded">
                <span>Terms of Service</span>
                <ExternalLink className="h-4 w-4" />
              </a>
            </CardContent>
          </Card>
        </div>

        {/* FAQ and Contact Form */}
        <div className="col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                Frequently Asked Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger>{faq.question}</AccordionTrigger>
                    <AccordionContent>{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Send us a message</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input
                    placeholder="Brief description of your issue"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea
                    placeholder="Describe your question or issue in detail..."
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Your Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}